import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TrendingDown, TrendingUp, ArrowLeftRight, Receipt, Wallet } from "lucide-react";
import { formatSEK0 } from "../lib/format";
import type { SpendingSummary as SummaryType, TransactionTypeFilter } from "../types";

interface SpendingSummaryProps {
  summary: SummaryType;
  /** When set, cards deep-link to the transactions behind their number */
  onCardClick?: (filter: TransactionTypeFilter) => void;
}

export function SpendingSummaryCards({ summary, onCardClick }: SpendingSummaryProps) {
  const net = summary.totalIncome - summary.totalSpending;
  const cards: Array<{
    title: string;
    value: string;
    subtitle: string;
    icon: typeof Wallet;
    className: string;
    filter: TransactionTypeFilter;
  }> = [
    {
      title: "Utgifter",
      value: formatSEK0(summary.totalSpending),
      subtitle: `${summary.includedExpenseCount} räknas · ${summary.shownOnlyExpenseCount} visas bara`,
      icon: TrendingDown,
      className: "text-destructive",
      filter: "expenses",
    },
    {
      title: "Inkomster",
      value: formatSEK0(summary.totalIncome),
      subtitle: "Alla inkomster, överföringar exkl.",
      icon: TrendingUp,
      className: "text-success",
      filter: "income",
    },
    {
      title: "Netto",
      value: `${net >= 0 ? "+" : ""}${formatSEK0(net)}`,
      subtitle: "Inkomster - inkluderade utgifter",
      icon: Wallet,
      className: net >= 0 ? "text-success" : "text-destructive",
      filter: "counted",
    },
    {
      title: "Transaktioner",
      value: summary.transactionCount.toString(),
      subtitle: "Som ingår i översikten",
      icon: Receipt,
      className: "text-primary",
      filter: "counted",
    },
    {
      title: "Överföringar (exkl.)",
      value: summary.transferCount.toString(),
      subtitle: "Räknas aldrig i statistik",
      icon: ArrowLeftRight,
      className: "text-muted-foreground",
      filter: "transfers",
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((card) => (
        <Card
          key={card.title}
          role={onCardClick ? "button" : undefined}
          tabIndex={onCardClick ? 0 : undefined}
          title={onCardClick ? "Visa transaktionerna bakom siffran" : undefined}
          className={
            onCardClick
              ? "cursor-pointer transition-colors hover:border-primary/40 focus-visible:border-primary/60 focus-visible:outline-none"
              : undefined
          }
          onClick={onCardClick ? () => onCardClick(card.filter) : undefined}
          onKeyDown={
            onCardClick
              ? (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onCardClick(card.filter);
                  }
                }
              : undefined
          }
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
            <card.icon className={`h-4 w-4 ${card.className}`} />
          </CardHeader>
          <CardContent>
            <p className={`text-xl font-bold tabular-nums lg:text-2xl ${card.className}`}>
              {card.value}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {card.subtitle}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
